import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearch } from "wouter";
import { useAuth } from "@clerk/react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { CheckCircle, Inbox } from "lucide-react";
import { format } from "date-fns";
import { useOrg } from "@/lib/orgContext";

interface InboxItem {
  approvalId: number;
  contractId: number;
  coachName: string;
  sport: string;
  stage: string;
  submittedAt: string | null;
  submittedBy: string | null;
  annualSalary: number;
  waitingOnYou: boolean;
  documentKind?: "term_sheet" | "contract";
}

const STAGE_LABEL: Record<string, string> = {
  finance_review: "Finance Review",
  hr_review: "HR Review",
  sport_admin_review: "Sport Admin Review",
  legal_review: "Legal Review",
  compliance_review: "Compliance Review",
  ad_review: "AD Review",
  signature: "Signature",
};

type StageFilter = "all" | "ad_review";

export default function Approvals() {
  const { getToken } = useAuth();
  const { org } = useOrg();
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(true);

  // ?persona=ad deep-links from the AD dashboard tile pre-select the AD-only
  // filter chip. Plain visits land on "All" so the inbox keeps its existing
  // behavior for everyone else.
  const search = useSearch();
  const initialFilter: StageFilter = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("persona") === "ad" ? "ad_review" : "all";
  }, [search]);
  const [filter, setFilter] = useState<StageFilter>(initialFilter);
  useEffect(() => { setFilter(initialFilter); }, [initialFilter]);

  // Filter chip is only offered to users who actually carry the AD persona
  // (or admins) — surfacing it for, say, a Finance reviewer would just be
  // confusing noise.
  const isAd = org?.role === "admin" || (org?.personas ?? []).includes("AD");

  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch("/api/approvals/inbox", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setItems(await res.json());
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { fetchInbox(); }, [fetchInbox]);

  const filteredItems = useMemo(
    () => filter === "ad_review" ? items.filter((i) => i.stage === "ad_review") : items,
    [items, filter],
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="page-header">
          <h1>Approvals Inbox</h1>
          <p>Contracts waiting on your decision.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <Inbox className="h-5 w-5 text-muted-foreground" /> Pending your review
            <Badge variant="secondary" className="ml-2">{loading ? "…" : filteredItems.length}</Badge>
          </CardTitle>
          <CardDescription>Open a contract to record an approve / reject / request-changes decision.</CardDescription>
          {isAd && (
            <div className="flex flex-wrap items-center gap-2 pt-2">
              <span className="text-[11px] text-muted-foreground uppercase tracking-wide">Filter</span>
              <Button
                size="sm"
                variant={filter === "all" ? "default" : "outline"}
                className="h-7 px-2 text-xs"
                onClick={() => setFilter("all")}
              >
                All stages
              </Button>
              <Button
                size="sm"
                variant={filter === "ad_review" ? "default" : "outline"}
                className="h-7 px-2 text-xs"
                onClick={() => setFilter("ad_review")}
              >
                AD review only
                <Badge variant="secondary" className="ml-1.5 h-4 text-[10px]">
                  {items.filter((i) => i.stage === "ad_review").length}
                </Badge>
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full skeleton-shimmer" />)}
            </div>
          ) : filteredItems.length === 0 ? (
            <EmptyState
              icon={CheckCircle}
              title="You're all caught up"
              description={
                filter === "ad_review"
                  ? "No contracts are currently parked at AD review."
                  : "No contracts are currently waiting on your approval."
              }
            />
          ) : (
            <div className="divide-y divide-border/50">
              {filteredItems.map((item) => (
                <div
                  key={item.approvalId}
                  className="flex items-center justify-between py-4 hover:bg-muted/20 transition-colors rounded-lg px-2 -mx-2"
                >
                  <div>
                    <div className="font-medium text-sm flex items-center gap-2">
                      <span>{item.coachName}</span>
                      <span
                        className="text-[10px] font-mono text-muted-foreground bg-muted/60 border rounded px-1 py-px"
                        title={`Contract ID #${item.contractId}`}
                      >
                        #{item.contractId}
                      </span>
                      {item.documentKind === "term_sheet" && (
                        <Badge
                          variant="outline"
                          className="text-[10px] uppercase tracking-wide border-indigo-500 text-indigo-600 dark:text-indigo-300"
                          title="Non-binding offer; routes Finance → HR → Sport Admin → Legal → AD"
                        >
                          Term Sheet
                        </Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground mt-0.5">
                      {item.sport}
                      {item.documentKind === "term_sheet"
                        ? item.annualSalary > 0
                          ? ` · proposed $${item.annualSalary.toLocaleString()}/yr`
                          : ""
                        : ` · $${item.annualSalary.toLocaleString()}/yr`}
                      {item.submittedAt ? ` · submitted ${format(new Date(item.submittedAt), "MMM d, yyyy")}` : ""}
                      {item.submittedBy ? ` by ${item.submittedBy}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{STAGE_LABEL[item.stage] ?? item.stage}</Badge>
                    <Button asChild size="sm">
                      <Link href={`/contracts/${item.contractId}`}>Review</Link>
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
